import Vue from 'vue';
import { isEqual } from 'lodash';
import templates from 'framework/router/page-templates';
import configs from 'framework/router/route-data-configs';

/**
 * Simple path join method
 * @param {string[]} parts - The path parts
 * @return {string} The resulting path
 */
function pathJoin(...parts) {
    const separator = '/';
    const replaceRe = new RegExp(`${ separator }+`, 'g');
    return parts.join(separator).replace(replaceRe, separator);
}

/**
 * Get the scrollBehavior of a list of routes
 * @param {any} routes - A list of vue route definitions
 * @return {function} The scroll behavior function
 */
function getScrollBehavior(routes) {
    const route = routes.matched.find(m => m.meta.scrollBehavior);

    if (route && route.meta && route.meta.scrollBehavior) {
        return route.meta.scrollBehavior;
    }

    return null;
}


/**
 * Compare two route definitions, ignoring irrelevant information such as hash
 * @access public
 * @param {object} r1 - A route record describing the previous location
 * @param {object} r2 - A route record describing the next location
 * @return {object} An object with values comparing certain aspects of the two records
 */
function compareRecords(r1, r2) {
    const hash = r1.hash === r2.hash;
    const name = r1.name === r2.name;
    const params = isEqual(r1.params, r2.params);
    const query = isEqual(r1.query, r2.query);

    return { hash, name, params, query };
}

export function createScrollBehavior() {
    const Log = Vue.log({ context: { location: 'vue-router' } });

    try {
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
        }
    } catch (error) {
        Log.warn(error);
    }

    return (to, from, savedPosition) => {
        const scrollBehavior = getScrollBehavior(to);

        return new Promise(resolve => {
            // Check if any matched route config has meta that requires scrolling to top
            if (scrollBehavior) {
                // cords will be used if no selector is provided,
                // or if the selector didn't match any element.
                const position = scrollBehavior(to, from, savedPosition);
                Log.info('Scrolling - Using custom determination method...', JSON.stringify(position));

                // resolve({ x: 0, y: 0 });
                resolve(position);
                return;
            }

            if (savedPosition) {
                Log.info('Scrolling - Returning to saved position...');
                resolve(savedPosition);
                return;
            }

            // Scroll to anchor by returning the selector
            if (to.hash) {
                Log.info('Scrolling - Returning to selector...');
                resolve({ selector: to.hash });
                return;
            }

            // if the returned position is falsy or an empty object,
            // will retain current scroll position.
            Log.info('Scrolling - Retaining current scroll position...');
            resolve({});
        });
    };
}

/**
 * Check two route definitions whether there are only hash changes
 * @param {object} r1 - A route record describing the previous location
 * @param {object} r2 - A route record describing the next location
 * @return {boolean} The result of the comparison analysis
 */
function isHashChange(r1, r2) {
    const { hash, name, params, query } = compareRecords(r1, r2);
    return !hash && name && params && query;
}

/**
 * Funtion called before vue router transition
 * @param {object} Log - Router logger
 * @param {string} initialRoute - The initial url
 * @return {function} The before each hook
 */
export function createBeforeEachHook(Log, initialRoute) {
    return function beforeEachHook(to, from, next) {
        const hasMatches = (Array.isArray(to.matched) && to.matched.length);
        const isInitialRoute = (initialRoute === pathJoin(globals.config.router.base, to.fullPath));

        // Simple link without corresponding route record
        if (!hasMatches && !isInitialRoute && !isHashChange(to, from)) {
            Log.debug('No matches found, following path url');
            window.location.href = pathJoin(globals.config.router.base, to.path);
            return;
        }

        // Registered route record
        Log.debug('Following matched route');
        next();
    };
}

/**
 * Route config creation helper
 * @param {Array} records - A route record array describing a list of available routes
 * @param {Array} middlewares - A list of middlewares to apply to each route (also recursively)
 * @param {Array} parents - The records' parental hierarchy, the first item being the oldest
 * @return {Array} The enhanced route records, ready for being used by `vue-router`
 */
export function decorateRecords(records = [], middlewares = [], parents = []) {
    return middlewares.reduce((acc, middleware) => acc.map(record => middleware(record, parents, middlewares)), records);
}

/**
 * Fetch all records of a route config
 * @param {string} config - The config name
 * @param {any[]} routes - A list of routes
 * @return {any[]} A list of routes
 */
export function getRecords({ config, routes }) {
    if (Array.isArray(routes)) {
        return routes;
    }

    return configs[config].routes;
}

/**
 * Preprocess a route record coming from the server as these must be JSON compliant data
 * @param {IRouteRecord} record - The route record coming from the server
 * @return {IRouteRecord} The transformed record
 */
export function prepareRecord(record) {
    const { redirect, alias, component } = record;

    if (!redirect && !alias) {
        record.component = templates[component];
    }

    return record;
}
